import type {
  CursorPage,
  ProblemDetails,
  ValidationResult,
} from "@led-signage/shared/common";
import type {
  ApproveRequest,
  AuditLogRecord,
  AuditLogsQuery,
  CreateDeviceRequest,
  CreateDisplayRequest,
  CurrentUser,
  DeviceSummary,
  Display,
  ReasonRequest,
  ReleaseSummary,
  RollbackRequest,
  UpdateDisplayRequest,
  DeviceReleaseProgress,
  LatestScreenshotResponse,
  ScreenshotRequest,
  CreateDeviceResponse,
  CreateNamedResourceRequest,
  CreatePublicationRequest,
  CreateRevisionRequest,
  CreateScheduleRevisionRequest,
  DisplayDetail,
  MediaAsset,
  MediaPreviewResponse,
  MediaQuery,
  MediaUploadCompleteRequest,
  MediaUploadIntentRequest,
  MediaUploadIntentResponse,
  Playlist,
  PlaylistDetail,
  PlaylistRevision,
  PlaybackEventRecord,
  PlaybackEventsQuery,
  PublicationRequest,
  PublicationRequestDetail,
  PublishResponse,
  Schedule,
  ScheduleDetail,
  ScheduleRevision,
  ScheduleRevisionDetail,
  SchedulesQuery,
  RejectRequest,
  UpdatePlaylistRevisionRequest,
  UpdateScheduleRevisionRequest,
} from "@led-signage/shared/management-api";

/**
 * 管理APIの契約型だけを使うクライアント。応答の形が `shared/` と食い違えば
 * 型検査で落ちるため、画面側が独自の想定を持たないようにする。
 *
 * 認証はCloudflare Accessが担う。ブラウザはAccessのcookieを送るだけで、
 * JWTやAPIキーをJavaScriptで保持しない。
 */

const BASE_PATH = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;

  constructor(status: number, code: string, message: string, requestId: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.status === "number" &&
    typeof candidate.code === "string" &&
    typeof candidate.detail === "string"
  );
}

function requestId(): string {
  return crypto.randomUUID();
}

async function failure(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // problem+json を返せない経路（Access のリダイレクト等）も想定する
  }
  if (isProblemDetails(body)) {
    return new ApiError(body.status, body.code, body.detail, body.requestId);
  }
  if (response.status === 401) {
    return new ApiError(
      401,
      "AUTHENTICATION_REQUIRED",
      "サインインの有効期限が切れました。画面を再読み込みしてください。",
      response.headers.get("x-request-id"),
    );
  }
  return new ApiError(
    response.status,
    "UNEXPECTED_RESPONSE",
    `予期しない応答を受け取りました (HTTP ${String(response.status)})`,
    response.headers.get("x-request-id"),
  );
}

interface RequestOptions {
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

function withQuery(path: string, query: RequestOptions["query"]): string {
  if (query === undefined) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

async function send<T>(
  baseUrl: string,
  method: "GET" | "POST" | "PUT" | "PATCH",
  path: string,
  options: RequestOptions & {
    body?: unknown;
    idempotencyKey?: string;
    ifMatch?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "X-Request-Id": requestId(),
    // Accessが期限切れのSPA requestをlogin redirectではなく401で返すために必要。
    "X-Requested-With": "XMLHttpRequest",
  };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotencyKey !== undefined) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  if (options.ifMatch !== undefined) headers["If-Match"] = options.ifMatch;

  const response = await fetch(`${baseUrl}${withQuery(path, options.query)}`, {
    method,
    headers,
    // Cloudflare Access の cookie を送るために必須
    credentials: "include",
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

/** 画面が必要とする読み取り・書き込みだけを公開する。 */
export interface ManagementApi {
  listMedia(query?: MediaQuery, signal?: AbortSignal): Promise<CursorPage<MediaAsset>>;
  createMediaUploadIntent(
    input: MediaUploadIntentRequest,
    idempotencyKey: string,
  ): Promise<MediaUploadIntentResponse>;
  completeMediaUpload(
    mediaId: string,
    input: MediaUploadCompleteRequest,
  ): Promise<MediaAsset>;
  archiveMedia(mediaId: string, input: ReasonRequest): Promise<MediaAsset>;
  getMediaPreview(
    mediaId: string,
    signal?: AbortSignal,
  ): Promise<MediaPreviewResponse>;
  listPlaylists(signal?: AbortSignal): Promise<CursorPage<Playlist>>;
  createPlaylist(
    input: CreateNamedResourceRequest,
    idempotencyKey: string,
  ): Promise<Playlist>;
  getPlaylist(id: string, signal?: AbortSignal): Promise<PlaylistDetail>;
  createPlaylistRevision(
    playlistId: string,
    input: CreateRevisionRequest,
    idempotencyKey: string,
  ): Promise<PlaylistRevision>;
  updatePlaylistRevision(
    revisionId: string,
    input: UpdatePlaylistRevisionRequest,
    etag: string,
  ): Promise<PlaylistRevision>;
  validatePlaylistRevision(revisionId: string): Promise<ValidationResult>;
  listSchedules(
    query?: SchedulesQuery,
    signal?: AbortSignal,
  ): Promise<CursorPage<Schedule>>;
  createSchedule(
    input: CreateNamedResourceRequest,
    idempotencyKey: string,
  ): Promise<Schedule>;
  getSchedule(id: string, signal?: AbortSignal): Promise<ScheduleDetail>;
  createScheduleRevision(
    scheduleId: string,
    input: CreateScheduleRevisionRequest,
    idempotencyKey: string,
  ): Promise<ScheduleRevision>;
  getScheduleRevision(
    revisionId: string,
    signal?: AbortSignal,
  ): Promise<ScheduleRevisionDetail>;
  updateScheduleRevision(
    revisionId: string,
    input: UpdateScheduleRevisionRequest,
    etag: string,
  ): Promise<ScheduleRevision>;
  validateScheduleRevision(revisionId: string): Promise<ValidationResult>;
  createPublicationRequest(
    input: CreatePublicationRequest,
    idempotencyKey: string,
  ): Promise<PublicationRequest>;
  submitPublicationRequest(
    id: string,
    idempotencyKey: string,
  ): Promise<PublicationRequest>;
  publishPublicationRequest(
    id: string,
    idempotencyKey: string,
  ): Promise<PublishResponse>;
  createDevice(
    displayId: string,
    input: CreateDeviceRequest,
    idempotencyKey: string,
  ): Promise<CreateDeviceResponse>;
  getCurrentUser(signal?: AbortSignal): Promise<CurrentUser>;
  listDisplays(signal?: AbortSignal): Promise<CursorPage<DisplayDetail>>;
  listPublicationRequests(
    signal?: AbortSignal,
  ): Promise<CursorPage<PublicationRequest>>;
  getPublicationRequest(id: string, signal?: AbortSignal): Promise<PublicationRequestDetail>;
  listAuditLogs(
    query?: AuditLogsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPage<AuditLogRecord>>;
  listPlaybackEvents(
    query?: PlaybackEventsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPage<PlaybackEventRecord>>;
  approvePublicationRequest(
    id: string,
    input: ApproveRequest,
    idempotencyKey: string,
  ): Promise<PublicationRequest>;
  rejectPublicationRequest(
    id: string,
    input: RejectRequest,
    idempotencyKey: string,
  ): Promise<PublicationRequest>;
  createDisplay(
    input: CreateDisplayRequest,
    idempotencyKey: string,
  ): Promise<Display>;
  getDisplay(displayId: string, signal?: AbortSignal): Promise<DisplayDetail>;
  updateDisplay(
    displayId: string,
    input: UpdateDisplayRequest,
    etag: string,
  ): Promise<Display>;
  archiveDisplay(
    displayId: string,
    input: ReasonRequest,
    idempotencyKey: string,
  ): Promise<Display>;
  revokeDevice(deviceId: string, input: ReasonRequest): Promise<DeviceSummary>;
  rollbackDisplay(
    displayId: string,
    input: RollbackRequest,
    idempotencyKey: string,
  ): Promise<ReleaseSummary>;
  getDeviceReleaseStatus(
    deviceId: string,
    signal?: AbortSignal,
  ): Promise<readonly DeviceReleaseProgress[]>;
  getLatestScreenshot(
    deviceId: string,
    signal?: AbortSignal,
  ): Promise<LatestScreenshotResponse>;
  requestScreenshot(
    deviceId: string,
    idempotencyKey: string,
  ): Promise<ScreenshotRequest>;
}

/**
 * 承認・差し戻しは再送しても二重に適用されてはならない。呼び出し側が操作ごとに
 * 1つ鍵を作り、失敗して再試行するときも同じ鍵を使い回す。
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function createManagementApi(baseUrl: string): ManagementApi {
  const root = `${baseUrl}${BASE_PATH}`;
  return {
    listMedia: (query, signal) =>
      send(root, "GET", "/media", {
        signal,
        query: query as RequestOptions["query"],
      }),
    createMediaUploadIntent: (input, idempotencyKey) =>
      send(root, "POST", "/media/upload-intents", {
        body: input,
        idempotencyKey,
      }),
    completeMediaUpload: (mediaId, input) =>
      send(
        root,
        "POST",
        `/media/${encodeURIComponent(mediaId)}/complete`,
        { body: input },
      ),
    archiveMedia: (mediaId, input) =>
      send(root, "POST", `/media/${encodeURIComponent(mediaId)}/archive`, {
        body: input,
      }),
    getMediaPreview: (mediaId, signal) =>
      send(root, "GET", `/media/${encodeURIComponent(mediaId)}/preview`, {
        signal,
      }),
    listPlaylists: (signal) =>
      send(root, "GET", "/playlists", { signal }),
    createPlaylist: (input, idempotencyKey) =>
      send(root, "POST", "/playlists", { body: input, idempotencyKey }),
    getPlaylist: (id, signal) =>
      send(root, "GET", `/playlists/${encodeURIComponent(id)}`, { signal }),
    createPlaylistRevision: (playlistId, input, idempotencyKey) =>
      send(
        root,
        "POST",
        `/playlists/${encodeURIComponent(playlistId)}/revisions`,
        { body: input, idempotencyKey },
      ),
    updatePlaylistRevision: (revisionId, input, etag) =>
      send(
        root,
        "PUT",
        `/playlist-revisions/${encodeURIComponent(revisionId)}`,
        { body: input, ifMatch: etag },
      ),
    validatePlaylistRevision: (revisionId) =>
      send(
        root,
        "POST",
        `/playlist-revisions/${encodeURIComponent(revisionId)}/validate`,
      ),
    listSchedules: (query, signal) =>
      send(root, "GET", "/schedules", {
        signal,
        query: query as RequestOptions["query"],
      }),
    createSchedule: (input, idempotencyKey) =>
      send(root, "POST", "/schedules", { body: input, idempotencyKey }),
    getSchedule: (id, signal) =>
      send(root, "GET", `/schedules/${encodeURIComponent(id)}`, { signal }),
    createScheduleRevision: (scheduleId, input, idempotencyKey) =>
      send(
        root,
        "POST",
        `/schedules/${encodeURIComponent(scheduleId)}/revisions`,
        { body: input, idempotencyKey },
      ),
    getScheduleRevision: (revisionId, signal) =>
      send(
        root,
        "GET",
        `/schedule-revisions/${encodeURIComponent(revisionId)}`,
        { signal },
      ),
    updateScheduleRevision: (revisionId, input, etag) =>
      send(
        root,
        "PUT",
        `/schedule-revisions/${encodeURIComponent(revisionId)}`,
        { body: input, ifMatch: etag },
      ),
    validateScheduleRevision: (revisionId) =>
      send(
        root,
        "POST",
        `/schedule-revisions/${encodeURIComponent(revisionId)}/validate`,
      ),
    createPublicationRequest: (input, idempotencyKey) =>
      send(root, "POST", "/publication-requests", { body: input, idempotencyKey }),
    submitPublicationRequest: (id, idempotencyKey) =>
      send(
        root,
        "POST",
        `/publication-requests/${encodeURIComponent(id)}/submit`,
        { body: {}, idempotencyKey },
      ),
    publishPublicationRequest: (id, idempotencyKey) =>
      send(
        root,
        "POST",
        `/publication-requests/${encodeURIComponent(id)}/publish`,
        { body: {}, idempotencyKey },
      ),
    createDevice: (displayId, input, idempotencyKey) =>
      send(
        root,
        "POST",
        `/displays/${encodeURIComponent(displayId)}/devices`,
        { body: input, idempotencyKey },
      ),
    getCurrentUser: (signal) => send(root, "GET", "/me", { signal }),
    createDisplay: (input, idempotencyKey) =>
      send(root, "POST", "/displays", { body: input, idempotencyKey }),
    getDisplay: (displayId, signal) =>
      send(root, "GET", `/displays/${encodeURIComponent(displayId)}`, { signal }),
    // 楽観ロック。他の担当者が先に更新していれば412で弾かれる。
    updateDisplay: (displayId, input, etag) =>
      send(root, "PATCH", `/displays/${encodeURIComponent(displayId)}`, {
        body: input,
        ifMatch: etag,
      }),
    archiveDisplay: (displayId, input, idempotencyKey) =>
      send(root, "POST", `/displays/${encodeURIComponent(displayId)}/archive`, {
        body: input,
        idempotencyKey,
      }),
    rollbackDisplay: (displayId, input, idempotencyKey) =>
      send(root, "POST", `/displays/${encodeURIComponent(displayId)}/rollback`, {
        body: input,
        idempotencyKey,
      }),
    revokeDevice: (deviceId, input) =>
      send(root, "POST", `/devices/${encodeURIComponent(deviceId)}/revoke`, {
        body: input,
      }),
    getDeviceReleaseStatus: (deviceId, signal) =>
      send(root, "GET", `/devices/${encodeURIComponent(deviceId)}/release-status`, {
        signal,
      }),
    getLatestScreenshot: (deviceId, signal) =>
      send(root, "GET", `/devices/${encodeURIComponent(deviceId)}/screenshots/latest`, {
        signal,
      }),
    requestScreenshot: (deviceId, idempotencyKey) =>
      send(
        root,
        "POST",
        `/devices/${encodeURIComponent(deviceId)}/screenshots/requests`,
        { body: {}, idempotencyKey },
      ),
    listDisplays: (signal) => send(root, "GET", "/displays", { signal }),
    listPublicationRequests: (signal) =>
      send(root, "GET", "/publication-requests", { signal }),
    getPublicationRequest: (id, signal) =>
      send(root, "GET", `/publication-requests/${encodeURIComponent(id)}`, { signal }),
    listAuditLogs: (query, signal) =>
      send(root, "GET", "/audit-logs", {
        signal,
        query: query as RequestOptions["query"],
      }),
    listPlaybackEvents: (query, signal) =>
      send(root, "GET", "/playback-events", {
        signal,
        query: query as RequestOptions["query"],
      }),
    approvePublicationRequest: (id, input, idempotencyKey) =>
      send(
        root,
        "POST",
        `/publication-requests/${encodeURIComponent(id)}/approve`,
        { body: input, idempotencyKey },
      ),
    rejectPublicationRequest: (id, input, idempotencyKey) =>
      send(
        root,
        "POST",
        `/publication-requests/${encodeURIComponent(id)}/reject`,
        { body: input, idempotencyKey },
      ),
  };
}
